import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo } from 'react';
import { FeedArticle, setProposalVoteStatus } from '@/views/Stream';
import { useCurrentAddressInfo, useDeviceTypeIsMobile, useSwitch, useTheme } from '@/hooks';
import clsx from 'clsx';
import Button from '@/components/Button';
import styles from './index.module.scss';
import {
  formatRanking,
  isInDebankApp,
  login,
  openInDebank,
  openNewWindow,
  shortenAddress,
} from '@/utils';
import { useVoteProposal } from '@/views/Proposal/hooks';
import { IconRcStreamDrawHot } from '@/assets/stream';
import { IconRcVoteUsd } from '@/assets/proposals';
import { ProposalVoterModal } from '../ProposalVoterModal';
import { MobileModalTip } from '@/components/shared/MobileModalTip';
import { HelperTooltip } from '@/components';

export const ProposalCard = ({ article }: { article: FeedArticle }) => {
  const { t } = useTranslation();
  const { proposal } = article;
  const isMobile = useDeviceTypeIsMobile();
  const { address, userInfo, isVerified } = useCurrentAddressInfo();
  const { on: isShowVoterList, turnOn: showVoterList, turnOff: hideVoterList } = useSwitch();
  const { isDark } = useTheme();
  const { on: hoverBtn, turnOn, turnOff } = useSwitch();
  const { on: mobileTip, turnOn: showMobileTip, turn: setMobileTip } = useSwitch();
  const { mutate, isLoading } = useVoteProposal({
    hasVoted: !!proposal?.is_voter,
    source: 'stream',
  });

  const creatorName = useMemo(
    () => proposal?.creator?.desc?.user?.web3_id || shortenAddress(proposal?.creator_id || ''),
    [proposal?.creator?.desc?.user?.web3_id, proposal?.creator_id],
  );

  const handleClick = useCallback(
    e => {
      e.stopPropagation();
      if (isMobile) {
        openInDebank({ link: `vote/${proposal?.id}` });
        return;
      }
      if (!isVerified) {
        login();
        return;
      }
      if (isLoading) {
        return;
      }
      mutate(proposal?.id!, {
        onSuccess() {
          setProposalVoteStatus({
            currentAddress: address!,
            articleId: article.id,
            usdValue: userInfo?.desc?.usd_value || 0,
          });
        },
      });
    },
    [
      address,
      article.id,
      isLoading,
      isMobile,
      isVerified,
      mutate,
      proposal?.id,
      userInfo?.desc?.usd_value,
    ],
  );

  const handleViewProposal = useCallback(
    e => {
      e.stopPropagation();
      if (isInDebankApp()) {
        openInDebank({ link: `vote/${proposal?.id}` });
        return;
      }
      openNewWindow(`${window.location.origin}/proposal/${proposal?.id}`);
    },
    [proposal?.id],
  );
  const handelShowVoters = useCallback(
    e => {
      e.stopPropagation();
      if (isMobile) {
        showMobileTip();
        return;
      }
      showVoterList();
    },
    [isMobile, showMobileTip, showVoterList],
  );

  const disabledVote = useMemo(() => proposal?.status === 'finished', [proposal?.status]);

  const handleHover = useCallback(() => {
    if (disabledVote) {
      return;
    }
    turnOn();
  }, [disabledVote, turnOn]);

  const handleHoverLeave = useCallback(() => {
    if (disabledVote) {
      return;
    }
    turnOff();
  }, [disabledVote, turnOff]);

  return (
    <div className={clsx(isMobile && styles.mobile)}>
      <div className={clsx(styles.proposalCard, isDark && styles.isDark)}>
        <div className={styles.creator}>{t('ProposalCard.cjzitj', { name: creatorName })}</div>
        <div className={styles.title} onClick={handleViewProposal}>
          <span className={styles.titleText}>
            {isMobile ? t('ProposalCard.view') : t('ProposalCard.viewDetail')}
            {'>'}
          </span>
        </div>
        <div className={styles.body}>
          <div className={styles.proposalTitle}>{proposal?.description}</div>
          <div className={styles.proposalDetail}>
            {proposal?.url_list?.length ? (
              <>
                <div>{t('ProposalCard.4aj268')}</div>
                {proposal?.url_list?.map((x, i) => (
                  <p key={i}>
                    <a
                      className={styles.relatedLink}
                      onClick={e => e.stopPropagation()}
                      href={x}
                      target="_blank"
                      rel="noreferrer">
                      {x}
                    </a>
                  </p>
                ))}
              </>
            ) : null}
            {proposal?.addr_list?.length ? (
              <>
                <div className={styles.relatedAddress}>{t('ProposalCard.j3i2bf')}</div>
                {proposal?.addr_list?.map((x, i) => <div key={i}>{x}</div>)}
              </>
            ) : null}
            {proposal?.issue_addr ? (
              <>
                <div className={styles.relatedAddress}>{t('ProposalCard.u904c2')}</div>
                <div>{proposal?.issue_addr}</div>
              </>
            ) : null}
          </div>
          <div className={styles.voteBtnContainer}>
            <HelperTooltip title={disabledVote ? t('ProposalCard.disabledTip') : undefined}>
              <Button
                type="primary"
                onMouseEnter={handleHover}
                onMouseLeave={handleHoverLeave}
                onClick={handleClick}
                disabled={disabledVote}
                className={clsx(
                  styles.voteBtn,
                  proposal?.is_voter && styles.voted,
                  disabledVote && styles.disabled,
                )}>
                {!proposal?.is_voter
                  ? t('ProposalCard.vote')
                  : hoverBtn
                    ? t('ProposalCard.cancelVote')
                    : t('ProposalCard.voted')}
              </Button>
            </HelperTooltip>
          </div>
        </div>
        <div className={styles.footer}>
          <div className={styles.status} onClick={handelShowVoters}>
            <IconRcStreamDrawHot className={styles.statusIcon} />
            <span className={styles.statusText}>
              {t('ProposalCard.voter', { count: proposal?.voter_count })}
            </span>
          </div>
          {isMobile ? <div className={styles.divider} /> : null}
          <div className={styles.status} onClick={handelShowVoters}>
            <IconRcVoteUsd className={styles.statusIcon} />
            <span className={styles.statusText}>{formatRanking(proposal?.usd_value)}</span>
          </div>
        </div>
      </div>
      {isShowVoterList ? (
        <ProposalVoterModal id={proposal?.id!} on={isShowVoterList} turnOff={hideVoterList} />
      ) : null}
      {isMobile && mobileTip ? (
        <MobileModalTip visible={mobileTip} setVisible={setMobileTip} />
      ) : null}
    </div>
  );
};
